<flx-modal
  [dismissible]="true"
  [class.medium]="isDesignerUser"
  [showBack]="!editUserStep()"
  (back)="editUserStep.set(true)"
  (dismiss)="closeUserModal()"
  class="flx-ds"
>
  @if (combinedLoading()) {
    <div
      class="absolute top-0 left-0 flex h-full w-full items-center justify-center bg-black/20 z-10"
    >
      <flx-spinner size="small"></flx-spinner>
    </div>
  }

  <div
    class="flx-modal-title"
    flxModalHeaderTitle
  >
    {{ title() }}
  </div>
  @if (user()) {
    <flx-user-summary
      [user]="user()"
      class="flx-modal-subtitle"
      flxModalHeaderSubtitle
    />
  }
  @if (editUserStep()) {
    <div
      [formGroup]="userForm"
      [conditionalControlTypes]="['p-autoComplete']"
      (submitForm)="onSave()"
      class="flx-modal-body gap-4"
      flxEnterSubmitForm
    >
      <flx-input
        [class.hidden]="isEditMode"
        class="flx-ds basis-1/2 min-w-0"
      >
        <flx-label class="flx-ds mandatory">Email</flx-label>
        <input
          flxInput
          formControlName="email"
          placeholder="Email"
          data-testId="email-input"
        />
        @if (submitted() && userForm.controls.email.errors) {
          <flx-error>
            @if (userForm.controls.email.errors.required) {
              Email is required
            } @else if (userForm.controls.email.errors.email) {
              Email must be valid
            }
          </flx-error>
        }
      </flx-input>

      <div class="flex flex-row gap-4">
        <flx-input class="flx-ds basis-1/2 min-w-0">
          <flx-label class="flx-ds mandatory">First name</flx-label>
          <input
            flxInput
            formControlName="firstName"
            placeholder="First name"
            data-testId="first-name-input"
          />
          @if (submitted() && userForm.controls.firstName.errors) {
            <flx-error>First name is required</flx-error>
          }
        </flx-input>
        <flx-input class="flx-ds basis-1/2 min-w-0">
          <flx-label class="flx-ds mandatory">Last name</flx-label>
          <input
            flxInput
            formControlName="lastName"
            placeholder="Last name"
            data-testId="last-name-input"
          />
          @if (submitted() && userForm.controls.lastName.errors) {
            <flx-error>Last name is required</flx-error>
          }
        </flx-input>
      </div>

      <div
        [class.hidden]="isEditMode"
        class="flex flex-row gap-4 items-start"
      >
        <flx-input class="flx-ds basis-1/2">
          <flx-label class="flx-ds mandatory">Password</flx-label>
          <input
            [type]="showPassword() ? 'text' : 'password'"
            placeholder="Enter password"
            flxInput
            formControlName="password"
            data-testId="password-input"
          />
          <button
            [disabled]="!canEdit"
            (click)="showPassword.set(!showPassword())"
            class="flx-ds p-0 neutral w-fit text-lg"
            variant="text"
            type="button"
            flxButton
          >
            <span
              [ngClass]="showPassword() ? 'ph-eye' : 'ph-eye-slash'"
              class="ph"
            ></span>
          </button>

          @if (submitted() && (userForm.controls.password.errors || userForm.errors?.sameAsEmail)) {
            <flx-error>
              @if (userForm.controls.password.errors) {
                @if (userForm.controls.password.errors.required) {
                  {{ passwordErrors.required }}
                } @else if (userForm.controls.password.errors.minlength) {
                  {{ passwordErrors.minlength }}
                } @else if (userForm.controls.password.errors.maxlength) {
                  {{ passwordErrors.maxlength }}
                } @else if (userForm.controls.password.errors.pattern) {
                  {{ passwordErrors.pattern }}
                }
              } @else if (userForm.errors?.sameAsEmail) {
                {{ passwordErrors.sameAsEmail }}
              }
            </flx-error>
          } @else {
            <flx-helper>{{ passwordHelperText }}</flx-helper>
          }
        </flx-input>

        <flx-input class="flx-ds basis-1/2">
          <flx-label class="flx-ds mandatory">Confirm password</flx-label>
          <input
            [type]="showConfirmPassword() ? 'text' : 'password'"
            flxInput
            placeholder="Confirm password"
            formControlName="confirmPassword"
            data-testId="confirm-password-input"
          />
          <button
            (click)="showConfirmPassword.set(!showConfirmPassword())"
            class="flx-ds p-0 neutral w-fit text-lg"
            variant="text"
            type="button"
            flxButton
          >
            <span
              [ngClass]="showConfirmPassword() ? 'ph-eye' : 'ph-eye-slash'"
              class="ph"
            ></span>
          </button>
          @if (submitted() && userForm.controls.confirmPassword.errors) {
            <flx-error>
              @if (userForm.controls.confirmPassword.errors.required) {
                Confirm password is required
              } @else if (userForm.controls.confirmPassword.errors.passwordMismatch) {
                Passwords must match
              }
            </flx-error>
          }
        </flx-input>
      </div>

      @if (isDesignerUser) {
        <flx-label
          class="flx-ds text-base font-semibold"
          data-testid="assign-roles-label"
          >Assign Roles</flx-label
        >

        <flx-choice-group
          class="flx-ds radio-group"
          data-testId="select-org-role"
        >
          <flx-label class="flx-ds mandatory">Organization-Level Roles </flx-label>
          <div class="flex items-center gap-2.5">
            @for (
              organizationRoleOption of organizationRoleOptions;
              track organizationRoleOption.value
            ) {
              <flx-radio
                [clear]="true"
                [value]="organizationRoleOption.value"
                [checked]="orgRoleIdValue() === organizationRoleOption.value"
                [attr.data-testId]="'org-choice-' + organizationRoleOption.label"
                class="flx-ds basis-1/3"
                formControlName="orgRoleId"
              >
                <flx-label class="flx-ds">
                  {{ organizationRoleOption.label }}
                  <p class="font-normal text-xs text-neutral-500">
                    {{ organizationRoleOption.description }}
                  </p>
                </flx-label>
              </flx-radio>
            }
          </div>
        </flx-choice-group>
        @if (roleChangeImpactMessage()) {
          <flx-message
            class="flx-ds"
            type="error"
            display="fill"
            data-testId="warning-message-body"
          >
            <i
              class="ph ph-warning"
              flxMessageError
            ></i>
            <div class="text-xs">
              <p>{{ roleChangeImpactMessage().title }}</p>
              <p class="font-normal">{{ roleChangeImpactMessage().description }}</p>
            </div>
          </flx-message>
        }

        <div class="flex flex-col gap-2">
          <div class="flex items-center relative">
            <flx-label
              [class.mandatory]="!orgRoleIdValue()"
              class="flx-ds"
              >Workspace-Level Roles</flx-label
            >
            <button
              [class.hidden]="helperBoxContent() || !canEdit"
              [disabled]="workspaceAssignmentsStatusInvalid()"
              (click)="workspaceAssignments.addWorkspaceAssignment()"
              class="flx-ds absolute right-0"
              data-testId="add-workspace-assignment-button"
              flxButton
              type="button"
              variant="text"
            >
              Add Workspace
            </button>
          </div>
          <flx-idea-helper-box [class.hidden]="!helperBoxContent()">
            {{ helperBoxContent() }}
          </flx-idea-helper-box>
          <flx-workspace-assignments
            #workspaceAssignments
            [availableWorkspacesForUser]="availableWorkspacesForUser()"
            [userRoleOptions]="userRoleOptions()"
            [enableErrorValidation]="submitted()"
            [class.hidden]="helperBoxContent()"
            formControlName="workspaceAssignments"
          />
        </div>
      }

      <flx-user-runtime-options
        [userModalFG]="userForm"
        [canEdit]="canEdit"
        [rolesVisible]="rolesVisible"
        [groupsVisible]="groupsVisible"
        [attributesVisible]="attributesVisible"
        [attributesDisabled]="attributesDisabled"
        [persistedAttributes]="persistedAttributes()"
        [enableErrorValidation]="submitted()"
        [isSelfEdit]="isSelfEdit"
        [usersType]="usersType"
        (rolesIsLoading)="rolesIsLoading.set($event)"
        (groupsIsLoading)="groupsIsLoading.set($event)"
      />
    </div>

    <div class="flx-modal-footer">
      @if (canChangePassword() && !combinedLoading()) {
        <div class="flx-modal-footer__left">
          <button
            (click)="editUserStep.set(false)"
            class="flx-ds secondary mr-auto"
            data-testId="update-password-button"
            flxButton
            variant="text"
          >
            Change password
          </button>
        </div>
      }
      <div class="flx-modal-footer__right">
        <button
          (click)="closeUserModal()"
          class="flx-ds secondary"
          data-testId="cancel-button"
          flxButton
          type="button"
        >
          Cancel
        </button>
        <button
          [disabled]="!canEdit"
          (click)="onSave()"
          class="flx-ds primary"
          data-testId="save-button"
          flxButton
          type="button"
        >
          {{ isEditMode ? 'Save Changes' : 'Create User' }}
        </button>
      </div>
    </div>
  } @else {
    <div class="flx-modal-body">
      <flx-change-password
        #changePassword
        [userId]="$safeNavigationMigration(user()?.id)"
        [email]="userForm.controls.email.value"
        [usersType]="usersType"
        (closeModal)="editUserStep.set(true)"
      />
    </div>

    <div class="flx-modal-footer">
      <div class="flx-modal-footer__right">
        <button
          (click)="editUserStep.set(true)"
          class="flx-ds secondary"
          data-testId="cancel-button"
          flxButton
          type="button"
        >
          Back
        </button>
        <button
          (click)="changePassword.submit()"
          class="flx-ds primary"
          data-testId="save-button"
          flxButton
          type="button"
        >
          Change Password
        </button>
      </div>
    </div>
  }
</flx-modal>
